import { Component } from '@theme/component';
import { debounce, onDocumentLoaded } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * Hover intent: how long a mouse must rest on a top-level item before its mega menu opens.
 * Prevents a panel from flying open when the cursor merely sweeps across the nav.
 * Bypassed for keyboard focus, touch/pen input, and when a menu is already open — see `activate`.
 */
const ACTIVATE_DELAY = 200;
const DEACTIVATE_DELAY = 350;

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #abortController = new AbortController();
  #scrollLockActive = false;

  connectedCallback() {
    super.connectedCallback();

    this.overflowMenu?.addEventListener('pointerleave', () => this.#debouncedDeactivate(), {
      signal: this.#abortController.signal,
    });

    document.addEventListener('pointerdown', (event) => {
      if (this.#state.activeItem?.hasAttribute('data-custom-build-toggle') && event.target instanceof Node && !this.contains(event.target)) {
        this.#deactivate(this.#state.activeItem, true);
      }
    }, { signal: this.#abortController.signal });

    onDocumentLoaded(this.#preloadImages);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#cancelPendingActivate();
    this.#unlockScroll();
  }

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * Time to allow for a closing animation between initiating a deactivation and actually deactivating the active item.
   * @returns {number}
   */
  get animationDelay() {
    const value = this.dataset.animationDelay;
    return value ? parseInt(value, 10) : 0;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow menu is hovered
   * @returns {boolean}
   */
  get overflowHovered() {
    return this.refs.overflowMenu?.matches(':hover') ?? false;
  }

  /**
   * Hover intent delay in ms. Override per header with `data-activate-delay`.
   * @returns {number}
   */
  get activateDelay() {
    const value = this.dataset.activateDelay;
    const parsed = value ? parseInt(value, 10) : NaN;

    return Number.isNaN(parsed) ? ACTIVATE_DELAY : parsed;
  }

  /**
   * Activate the selected menu item, waiting out the hover-intent delay when the pointer
   * is just passing through. Skips the delay when the user has already shown intent:
   * keyboard focus, touch/pen taps, and moving between items while a menu is open.
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    this.#debouncedDeactivate.cancel();
    this.#cancelPendingActivate();

    const isMouseHover = event instanceof PointerEvent && event.pointerType === 'mouse';
    const menuAlreadyOpen = this.#state.activeItem != null;
    const delay = this.activateDelay;

    if (!isMouseHover || menuAlreadyOpen || delay <= 0) {
      this.#activateHandler(event);
      return;
    }

    this.#pendingActivate = setTimeout(() => {
      this.#pendingActivate = null;
      this.#activateHandler(event);
    }, delay);
  };


  /** Separate Custom Builds page navigation from its disclosure control. */
  toggleSplitMenu(event) {
    event.preventDefault();
    this.#debouncedDeactivate.cancel();
    this.#cancelPendingActivate();
    const item = findMenuItem(event.target);
    if (!item?.hasAttribute('data-custom-build-toggle')) return;
    if (item === this.#state.activeItem) this.#deactivate(item, true);
    else this.#activateHandler({ target: item });
  }

  activateSplitFocus(event) {
    this.#debouncedDeactivate.cancel();
    if (event.target instanceof Element && event.target.closest('.menu-list__submenu')) {
      this.#activateHandler({ target: findMenuItem(event.target) });
    }
  }

  handleSplitKeydown(event) {
    if (event.key !== 'Escape') return;
    const item = findMenuItem(event.target);
    if (!item?.hasAttribute('data-custom-build-toggle')) return;
    event.preventDefault();
    event.stopPropagation();
    this.#cancelPendingActivate();
    this.#deactivate(item, true);
    item.focus();
  }

  /** @type {ReturnType<typeof setTimeout> | null} */
  #pendingActivate = null;

  /**
   * Drop a hover-intent open that never earned its delay.
   */
  #cancelPendingActivate = () => {
    if (this.#pendingActivate == null) return;

    clearTimeout(this.#pendingActivate);
    this.#pendingActivate = null;
  };

  /**
   * Open the submenu for the item behind the given event. Callers decide the timing.
   * @param {PointerEvent | FocusEvent} event
   */
  #activateHandler = (event) => {
    this.#debouncedDeactivate.cancel();

    this.dispatchEvent(new MegaMenuHoverEvent());

    this.removeAttribute('data-animating');

    if (!(event.target instanceof Element)) return;

    let item = findMenuItem(event.target);

    if (!item) return;

    if (item == this.#state.activeItem) {
      this.#lockScroll();
      return;
    }

    const isDefaultSlot = event.target.slot === '';

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    const previouslyActiveItem = this.#state.activeItem;

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);

    if (!submenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    const submenuHeight = this.#measureSubmenuHeight(submenu);

    this.style.setProperty('--submenu-height', `${submenuHeight}px`);
    this.style.setProperty('--submenu-opacity', '1');

    if (submenu) {
      this.#lockScroll();
    }
  };

  /**
   * Measure submenu height without clip-path collapsing the reading to 0.
   * @param {HTMLElement | null | undefined} submenu
   * @returns {number}
   */
  #measureSubmenuHeight(submenu) {
    if (!submenu) return 0;

    const inner = submenu.querySelector('.menu-list__submenu-inner');
    const measureTarget = inner instanceof HTMLElement ? inner : submenu;

    const isCustom = measureTarget.classList.contains('menu-list__submenu-inner--custom');

    // Unclip so offsetHeight reflects full content. Custom panels paint themselves,
    // and probing at 100vh inflates that backdrop into a white strip under the menu.
    if (!isCustom) this.style.setProperty('--submenu-height', '100vh');

    const height = measureTarget.offsetHeight;

    return isCustom ? height : height + 24;
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    // Leaving the item before the hover-intent delay elapses means the menu never opens.
    this.#cancelPendingActivate();

    if (!(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    // Make sure the item to be deactivated is still the active one. Ideally
    // we cancelled the pending activation before the item was changed, but just in case.
    if (item === this.#state.activeItem) {
      this.#debouncedDeactivate();
    }
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   */
  #deactivate = (item = this.#state.activeItem, force = false) => {
    if (!item || item != this.#state.activeItem) return;
    if (this.overflowHovered && !force) return;

    this.style.setProperty('--submenu-height', '0px');
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';
    item.setAttribute('data-animating', '');

    setTimeout(() => {
      item.removeAttribute('data-animating');
    }, Math.max(0, this.animationDelay - 150)); // Start header transition 150ms before submenu finishes

    this.#unlockScroll();
  };

  #lockScroll() {
    if (this.#scrollLockActive) return;

    document.documentElement.setAttribute('scroll-lock', '');
    this.#scrollLockActive = true;
  }

  #unlockScroll() {
    if (!this.#scrollLockActive) return;

    document.documentElement.removeAttribute('scroll-lock');
    this.#scrollLockActive = false;
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  #debouncedDeactivate = debounce(this.#deactivate, DEACTIVATE_DELAY);

  /**
   * Preload images that are set to load lazily.
   */
  #preloadImages = () => {
    const images = this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image) => image.removeAttribute('loading'));
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  if (element.matches('[slot="more"]')) {
    // Select the first overflowing menu item when hovering over the "More" item
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }

  const menuItem = element.closest('[ref="menuitem"]');
  if (menuItem instanceof HTMLElement) return menuItem;

  const listItem = element.closest('.menu-list__list-item');
  const nestedMenuItem = listItem?.querySelector('[ref="menuitem"]');
  return nestedMenuItem instanceof HTMLElement ? nestedMenuItem : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}
